/**
 * useExploreGuard — Global Android hardware back button "never exit" guard.
 *
 * Intended to be called ONCE at the App root (not per-route). It installs a
 * single persistent popstate listener that intercepts back presses when the
 * user is on a NON-Explore route (pathname !== "/") and navigates them back
 * to Explore instead of letting the WebView exit the app.
 *
 * On Explore ("/"), this handler is passive — HomePage's own popstate handler
 * manages tabs, in-page modals, and ensures the app never exits there.
 *
 * Sentinel management:
 *   On non-Explore routes the guard pushes a { _exploreGuard: true } sentinel.
 *   After every pop on a non-Explore route it re-pushes the sentinel so there
 *   is always at least one entry on the stack for the next back press.
 *
 *   On Explore the guard does NOT push sentinels — HomePage manages its own
 *   sentinel stack so the two guards don't double-up.
 *
 * Coordination with useBackButtonClose (overlay back presses):
 *   Overlays managed by useBackButtonClose push { overlay: key } sentinels ON
 *   TOP of the guard's sentinel. When the overlay sentinel is popped by the
 *   back button this handler fires. Because useBackButtonClose tracks overlay
 *   state in the coordinator (hasActiveOverlays()), the guard skips navigating
 *   to "/" and only re-pushes its sentinel — the user presses back once more
 *   to actually leave the page (first back closes the overlay, second goes home).
 *
 * Coordination with programmatic overlay close (cleanup history.back()):
 *   When an overlay closes via an in-app button useBackButtonClose calls
 *   history.back() to pop its sentinel. This fires a popstate. The guard
 *   catches it, calls peekCleanupBack() to read the signal (without starving
 *   other popstate listeners — e.g. HomePage's own handler — that also need
 *   to observe it during this same dispatch), and only re-pushes its
 *   sentinel without navigating.
 *
 * Usage (call once at App root):
 *   useExploreGuard();
 *
 * Per-route components must NOT call this hook — the single App-level instance
 * covers all routes.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  peekCleanupBack,
  hasActiveOverlays,
} from '@/utils/back-button-coordinator';

export function useExploreGuard(): void {
  const navigate = useNavigate();
  // Stable ref so the listener closure never becomes stale for navigate.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  useEffect(() => {
    // Whether we have already pushed a guard sentinel for the current
    // non-Explore visit. Prevents double-pushes on re-renders or quick
    // successive calls before a pop has happened.
    let sentinelPushed = false;

    function ensureSentinel() {
      if (!sentinelPushed && window.location.pathname !== '/') {
        window.history.pushState({ _exploreGuard: true }, '');
        sentinelPushed = true;
      }
    }

    const handlePopState = () => {
      const onExplore = window.location.pathname === '/';

      // Always read the cleanup-back signal regardless of route so stale
      // signals from a previous programmatic overlay close don't affect the
      // next non-Explore back press. This peeks (and arms the signal's
      // deferred drain) rather than consuming it synchronously, so other
      // popstate listeners in this same dispatch (e.g. HomePage's own
      // handler) still observe it too.
      const isCleanup = peekCleanupBack();

      // --- On Explore: guard is passive, HomePage's handler manages this. ---
      if (onExplore) {
        sentinelPushed = false; // reset so re-entering a non-Explore route re-pushes
        return;
      }

      // --- On a non-Explore route ---

      // Re-push a sentinel so the NEXT back press also fires popstate.
      sentinelPushed = false;
      ensureSentinel();

      // Was this a programmatic cleanup history.back() from useBackButtonClose?
      if (isCleanup) {
        return; // keep the sentinel, don't navigate
      }

      // Is an overlay currently open? Its useBackButtonClose already called
      // onClose(). Keep the sentinel — user presses back once more to leave.
      if (hasActiveOverlays()) {
        return;
      }

      // Real back press, no overlay, non-Explore route → go to Explore.
      navigateRef.current('/');
    };

    window.addEventListener('popstate', handlePopState);

    // Push an initial sentinel for the current route so the very first back
    // press fires popstate (rather than immediately exiting the WebView) when
    // the app is opened on a non-Explore route (e.g. a deep link).
    ensureSentinel();

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Empty deps — install once, never re-register.
}
